import { manifestApi } from "#/api/manifestApi";
import HawbLinkWarningsModal from "#/components/manifests/HawbLinkWarningsModal";
import HawbSelect from "#/components/manifests/HawbSelect";
import { showSuccessNotification } from "#/components/notifications";
import type { HawbOption, ItemLinkWarning } from "#/types/manifest";
import { logger } from "#/utils/logger";
import { formatHawbLabel } from "#/utils/manifestUtils";
import { Button, Modal, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AssignAwbModalProps {
  opened: boolean;
  onClose: () => void;
  /** The selected rows to link. */
  itemIds: number[];
  /** Runs after the link was written (clear selection, refresh the list). */
  onSuccess: () => void;
}

/**
 * Links the selected items to one consignment. Same flow as the item detail
 * page: the backend preview runs first, and when it finds something off
 * (items moving, count mismatch, validated consignment) the user must confirm
 * before anything changes.
 */
const AssignAwbModal = ({ opened, onClose, itemIds, onSuccess }: AssignAwbModalProps) => {
  const { t } = useTranslation();
  const [hawb, setHawb] = useState<HawbOption | null>(null);
  const [warnings, setWarnings] = useState<ItemLinkWarning[]>([]);
  const [busy, setBusy] = useState(false);

  // Start clean every time the modal is opened.
  useEffect(() => {
    if (opened) {
      setHawb(null);
      setWarnings([]);
      setBusy(false);
    }
  }, [opened]);

  const close = () => {
    if (busy) return;
    setWarnings([]);
    onClose();
  };

  const link = async (target: HawbOption) => {
    setBusy(true);
    try {
      const response = await manifestApi.itemLink(itemIds, target.id);
      const updated = response.data.updated ?? 0;
      showSuccessNotification({
        message:
          updated > 0
            ? t("items.assignAwb.success", { count: updated, hawb: formatHawbLabel(target) })
            : t("items.assignAwb.nothingChanged", { hawb: formatHawbLabel(target) }),
      });
      setWarnings([]);
      setBusy(false);
      onClose();
      onSuccess();
    } catch (error) {
      // The API client already shows the error toast; keep the modal open.
      logger.error("Failed to assign items to the consignment:", error);
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!hawb || itemIds.length === 0 || busy) return;
    setBusy(true);
    let found: ItemLinkWarning[];
    try {
      const response = await manifestApi.itemLinkPreview(itemIds, hawb.id);
      found = response.data.warnings ?? [];
    } catch (error) {
      logger.error("Failed to check the consignment link:", error);
      setBusy(false);
      return;
    }
    setBusy(false);
    if (found.length > 0) {
      setWarnings(found);
      return;
    }
    await link(hawb);
  };

  return (
    <>
      <Modal
        opened={opened && warnings.length === 0}
        onClose={close}
        title={t("items.assignAwb.title")}
        centered
      >
        <div className="flex flex-col gap-4">
          <Text size="sm">{t("items.assignAwb.description", { count: itemIds.length })}</Text>
          <HawbSelect
            label={t("items.assignAwb.pickerLabel")}
            required
            value={hawb?.id ?? null}
            onChange={(_id, option) => setHawb(option)}
            disabled={busy}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" color="black" onClick={close} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button
              color="black"
              onClick={() => void submit()}
              loading={busy}
              disabled={!hawb || itemIds.length === 0}
            >
              {t("items.assignAwb.submit")}
            </Button>
          </div>
        </div>
      </Modal>
      <HawbLinkWarningsModal
        opened={opened && warnings.length > 0}
        warnings={warnings}
        loading={busy}
        onCancel={() => {
          if (!busy) setWarnings([]);
        }}
        onConfirm={() => {
          if (hawb) void link(hawb);
        }}
      />
    </>
  );
};

export default AssignAwbModal;
